import React from 'react';
import { useState, useEffect, useRef } from 'react';
import { Chip, Box, useTheme, TextField, Button, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import { styled } from '@mui/material/styles';
import ChatService from '../../services/ChatService';
import MarkdownRenderer from 'react-markdown-renderer';
import AILogo from '../../assets/Sophia-Profile.png';
import SendIcon from '@mui/icons-material/Send';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { getDefaultSettings } from '../../config';
import ContextPopover from './ContextPopover';
import SettingsPopover from './SettingsPopover';

const FullscreenButton = styled(Button)(({ theme, isFullscreen }) => ({
    position: 'absolute',
    top: isFullscreen ? '16px' : '8px',
    left: isFullscreen ? '16px' : '8px',
    minWidth: '40px',
    width: '40px',
    height: '40px',
    padding: '8px',
    borderRadius: isFullscreen ? '12px' : '20px 0 12px 0',
    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
    backdropFilter: 'blur(15px)',
    border: '1px solid rgba(102, 126, 234, 0.3)',
    color: '#4a5568',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10001,
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2), 0 2px 6px rgba(0, 0, 0, 0.1)',
    '&:hover': {
        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.25) 0%, rgba(118, 75, 162, 0.25) 100%)',
        border: '1px solid rgba(102, 126, 234, 0.5)',
        transform: 'scale(1.05) translateY(-1px)',
        boxShadow: '0 6px 16px rgba(102, 126, 234, 0.3), 0 3px 8px rgba(0, 0, 0, 0.15)',
        color: '#667eea',
    },
    '&:active': {
        transform: 'scale(0.98)',
        transition: 'all 0.1s ease',
    },
    '& .MuiButton-startIcon': {
        margin: 0,
        fontSize: '18px',
    },
    // Hide on mobile devices
    [theme.breakpoints.down(600)]: {
        display: 'none',
    },
    ...theme.applyStyles('dark', {
        background: 'linear-gradient(135deg, rgba(26, 26, 46, 0.4) 0%, rgba(22, 33, 62, 0.4) 100%)',
        border: '1px solid rgba(102, 126, 234, 0.4)',
        color: '#e2e8f0',
        boxShadow: '0 4px 12px rgba(26, 26, 46, 0.3), 0 2px 6px rgba(0, 0, 0, 0.2)',
        '&:hover': {
            background: 'linear-gradient(135deg, rgba(26, 26, 46, 0.6) 0%, rgba(22, 33, 62, 0.6) 100%)',
            border: '1px solid rgba(102, 126, 234, 0.6)',
            color: '#a78bfa',
            boxShadow: '0 6px 16px rgba(26, 26, 46, 0.4), 0 3px 8px rgba(0, 0, 0, 0.3)',
        },
    }),
}));

const ChatWindow = styled(Box)(({ theme, isVisible }) => ({
    position: 'fixed',
    bottom: isVisible ? '20px' : '-100%',
    right: '20px',
    width: Math.max(window.innerWidth * 0.3, 400),
    height: Math.max(window.innerHeight * 0.8, 600),
    borderRadius: '20px',
    zIndex: 12000,
    pointerEvents: isVisible ? 'auto' : 'none',
    minWidth: '320px',
    minHeight: '400px',
    maxWidth: '90vw',
    maxHeight: '85vh',
    // Only apply transition to position changes, not width/height (resizing)
    transition: 'bottom 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275), right 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275), left 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275), top 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1), 0 8px 32px rgba(102, 126, 234, 0.15)',
    ...theme.applyStyles('dark', {
        background: 'rgba(11, 14, 20, 0.95)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(33, 39, 50, 0.5)',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4), 0 8px 32px rgba(26, 26, 46, 0.3)',
    }),
    // Responsive design for tablets
    [theme.breakpoints.down('md')]: {
        width: '360px',
        height: '580px',
        maxWidth: '85vw',
        maxHeight: '80vh',
    },
    // Responsive design for mobile landscape
    [theme.breakpoints.down('sm')]: {
        width: '340px',
        height: '520px',
        right: '15px',
        bottom: isVisible ? '15px' : '-100%',
        maxWidth: '90vw',
        maxHeight: '75vh',
        borderRadius: '16px',
    },
    // Responsive design for mobile portrait
    [theme.breakpoints.down(600)]: {
        width: '100vw',
        height: '100vh',
        right: 0,
        left: 0,
        top: isVisible ? '0' : '100vh',
        maxWidth: '100vw',
        maxHeight: '100vh',
        borderRadius: 0,
    },
    // Extra small screens (very small phones)
    '@media (max-width: 400px)': {
        width: '100vw',
        height: '100vh',
        right: 0,
        left: 0,
        top: isVisible ? '0' : '100vh',
        maxWidth: '100vw',
        maxHeight: '100vh',
        borderRadius: 0,
    },
}));

const ChatScrollArea = styled(Box)(({ theme }) => ({
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    '&::-webkit-scrollbar': {
        width: '8px',
    },
    '&::-webkit-scrollbar-track': {
        background: 'rgba(156, 163, 175, 0.1)',
        borderRadius: '10px',
    },
    '&::-webkit-scrollbar-thumb': {
        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.3) 0%, rgba(118, 75, 162, 0.3) 100%)',
        borderRadius: '10px',
        border: '2px solid transparent',
        backgroundClip: 'padding-box',
        '&:hover': {
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.5) 0%, rgba(118, 75, 162, 0.5) 100%)',
        },
    },
    ...theme.applyStyles('dark', {
        '&::-webkit-scrollbar-track': {
            background: 'rgba(33, 39, 50, 0.3)',
        },
        '&::-webkit-scrollbar-thumb': {
            background: 'linear-gradient(135deg, rgba(26, 26, 46, 0.5) 0%, rgba(22, 33, 62, 0.5) 100%)',
            '&:hover': {
                background: 'linear-gradient(135deg, rgba(26, 26, 46, 0.7) 0%, rgba(22, 33, 62, 0.7) 100%)',
            },
        },
    }),
}));

const MessageFeed = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
}));

const AIMessage = styled(Box)(({ theme }) => ({
    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(102, 126, 234, 0.2)',
    color: '#2d3748',
    alignSelf: 'flex-start',
    padding: '16px 20px',
    textAlign: 'left',
    borderRadius: '20px 20px 20px 6px',
    maxWidth: '85%',
    boxShadow: '0 4px 16px rgba(102, 126, 234, 0.1), 0 2px 8px rgba(0, 0, 0, 0.05)',
    transition: 'all 0.2s ease',
    '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: '0 6px 20px rgba(102, 126, 234, 0.15), 0 3px 10px rgba(0, 0, 0, 0.1)',
    },
    ...theme.applyStyles('dark', {
        background: 'linear-gradient(135deg, rgba(26, 26, 46, 0.4) 0%, rgba(22, 33, 62, 0.4) 100%)',
        border: '1px solid rgba(102, 126, 234, 0.3)',
        color: '#e2e8f0',
        boxShadow: '0 4px 16px rgba(26, 26, 46, 0.3), 0 2px 8px rgba(0, 0, 0, 0.2)',
        '&:hover': {
            boxShadow: '0 6px 20px rgba(26, 26, 46, 0.4), 0 3px 10px rgba(0, 0, 0, 0.3)',
        },
    }),
}));

const SystemMessage = styled(Box)(({ theme }) => ({
    background: 'rgba(156, 163, 175, 0.15)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(156, 163, 175, 0.2)',
    color: '#6b7280',
    alignSelf: 'center',
    padding: '10px 18px',
    borderRadius: '25px',
    fontSize: '0.9em',
    fontWeight: '500',
    boxShadow: '0 2px 8px rgba(156, 163, 175, 0.1)',
    transition: 'all 0.2s ease',
    ...theme.applyStyles('dark', {
        background: 'rgba(75, 85, 99, 0.2)',
        border: '1px solid rgba(156, 163, 175, 0.3)',
        color: '#9ca3af',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
    }),
}));

const UserMessage = styled(Box)(({ theme }) => ({
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#ffffff',
    alignSelf: 'flex-end',
    padding: '16px 20px',
    borderRadius: '20px 20px 6px 20px',
    maxWidth: '85%',
    boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3), 0 2px 8px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.2s ease',
    fontWeight: '500',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
    '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4), 0 3px 10px rgba(0, 0, 0, 0.15)',
    },
    ...theme.applyStyles('dark', {
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        boxShadow: '0 4px 16px rgba(26, 26, 46, 0.4), 0 2px 8px rgba(0, 0, 0, 0.3)',
        '&:hover': {
            boxShadow: '0 6px 20px rgba(26, 26, 46, 0.5), 0 3px 10px rgba(0, 0, 0, 0.4)',
        },
    }),
}));

const ChatInputContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
    backdropFilter: 'blur(10px)',
    ...theme.applyStyles('dark', {
        borderTop: '1px solid rgba(33, 39, 50, 0.3)',
        background: 'linear-gradient(135deg, rgba(26, 26, 46, 0.2) 0%, rgba(22, 33, 62, 0.2) 100%)',
    }),
    // Responsive design
    [theme.breakpoints.down('sm')]: {
        padding: '16px 20px',
        flexDirection: 'column',
        gap: '12px',
        alignItems: 'stretch',
    },
    [theme.breakpoints.down(600)]: {
        padding: '14px 16px',
        gap: '10px',
    },
    '@media (max-width: 400px)': {
        padding: '12px 14px',
        gap: '8px',
    },
}));

const ChatInputField = styled(TextField)(({ theme }) => ({
    width: '100%',
    '& .MuiOutlinedInput-root': {
        background: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(10px)',
        borderRadius: '16px',
        color: '#2d3748',
        fontWeight: '500',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '& fieldset': {
            borderColor: 'rgba(102, 126, 234, 0.2)',
            borderWidth: '1px',
        },
        '&:hover fieldset': {
            borderColor: 'rgba(102, 126, 234, 0.4)',
            boxShadow: '0 0 0 1px rgba(102, 126, 234, 0.1)',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#667eea',
            borderWidth: '2px',
            boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
        },
        ...theme.applyStyles('dark', {
            background: 'rgba(33, 39, 50, 0.8)',
            color: '#e2e8f0',
            '& fieldset': {
                borderColor: 'rgba(102, 126, 234, 0.3)',
            },
            '&:hover fieldset': {
                borderColor: 'rgba(102, 126, 234, 0.5)',
                boxShadow: '0 0 0 1px rgba(102, 126, 234, 0.2)',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#667eea',
                boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.2)',
            },
        }),
    },
    '& .MuiOutlinedInput-input': {
        padding: '14px 16px',
        '&::placeholder': {
            color: '#9ca3af',
            opacity: 1,
        },
    },
}));

const ChatInput = ({ onSend }) => {
    const [message, setMessage] = useState('');
    const theme = useTheme();
    useEffect(() => {
        ChatService.initialize(process.env.REACT_APP_RAG_URL+'/query');
    }, []);
    const handleKeyPress = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            if (message.trim()) {
                onSend(message);
                setMessage('');
            }
        }
    };

    return (
        <ChatInputContainer>
            <ChatInputField
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type a message..."
                multiline
                maxRows={4}
                variant="outlined"
                size="small"
            />
            <Button 
                variant="contained" 
                endIcon={<SendIcon />} 
                onClick={() => {onSend(message); setMessage('')}}
                sx={{
                    minWidth: '110px',
                    height: '48px',
                    ml: { xs: '0px', sm: '16px' },
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#ffffff',
                    fontWeight: '600',
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3), 0 2px 8px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                        background: 'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4), 0 3px 10px rgba(0, 0, 0, 0.15)',
                    },
                    '&:active': {
                        transform: 'translateY(0px)',
                        transition: 'all 0.1s ease',
                    },
                    '&:disabled': {
                        background: 'rgba(156, 163, 175, 0.3)',
                        color: 'rgba(156, 163, 175, 0.7)',
                        boxShadow: 'none',
                    },
                    // Responsive design
                    '@media (max-width: 600px)': {
                        width: '100%',
                        minWidth: 'unset',
                        fontSize: '0.9rem',
                        height: '44px',
                        width: '50px',
                    },
                    '@media (max-width: 400px)': {
                        height: '40px',
                        fontSize: '0.85rem',
                        width: '44px',
                    },
                    ...(theme.palette.mode === 'dark' && {
                        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #2d1b69 0%, #1e3a8a 100%)',
                        },
                    }),
                }}
            >
                {window.innerWidth > 600 ? 'Send' : ''}
            </Button>
        </ChatInputContainer>
    );
};

const proccessMessage = (message) => {
    return message.replace('#','##')
    .replaceAll('support@intellicampus.com', '[support@intellicampus.com](mailto:support@intellicampus.com)')
    .replaceAll('https://intellicampus.com', '[intellicampus.com](https://intellicampus.com)')
    .replaceAll('markdown', '')
    .replaceAll('```', '')
}

const Message = ({message,tempResponse,context,query,onOpenContext}) => {
    console.log("Message",context);
    if(message.type === 'System'){
        return <SystemMessage>⚙️ {message.messageBody}</SystemMessage>
    }
    if(message.type === 'AI'){
        return <AIMessage>
            <div style={{display: 'flex', gap: '10px' }}>
                <img src={AILogo} alt="AI Logo" style={{ width: '30px', height: '30px', marginTop: '15px' }} /> 
                <div>
                <MarkdownRenderer markdown={proccessMessage(message.messageBody)} />
                {context && context.length > 0 && (
                    <button
                        onClick={(e) => onOpenContext?.(e, context,query)}
                        title="View context"
                        aria-label="View context"
                        style={{fontSize: '15px', color: '#667eea', marginTop: '6px', backgroundColor: 'transparent', border: 'none', cursor: 'pointer',justifyContent: 'flex-end'}}
                    >
                        🛈 Source
                    </button>
                )}
              </div>
            </div>
            </AIMessage>
    }
    if(message.type === 'User'){
        return <UserMessage>{message.messageBody}</UserMessage>
    }
    if(message.type === 'Loading'){
        return <AIMessage><MarkdownRenderer markdown={tempResponse} /><CircularProgress size={20} /></AIMessage>
    }
};

const messageFeed = (messages,tempResponse,onOpenContext) => {
    return (
        <MessageFeed>
            {messages?.map((message,index) => (
                <Message key={index} message={message} tempResponse={tempResponse} context={message.context} query={message.query} onOpenContext={onOpenContext} />
            ))}
        </MessageFeed>  
    );
};

const CloseButton = styled(Button)(({ theme }) => ({
    minWidth: '40px',
    width: '40px',
    height: '40px',
    padding: '8px',
    borderRadius: '12px',
    background: 'rgba(255, 255, 255, 0.2)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    color: '#4a5568',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    zIndex: 10000, // Ensure it's above everything else
    ...theme.applyStyles('dark', {
        background: 'rgba(33, 39, 50, 0.4)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        color: '#e2e8f0',
    }),
    '&:hover': {
        background: 'rgba(255, 71, 87, 0.2)',
        border: '1px solid rgba(255, 71, 87, 0.4)',
        color: '#ff4757',
        transform: 'scale(1.05)',
        boxShadow: '0 4px 12px rgba(255, 71, 87, 0.3)',
        ...theme.applyStyles('dark', {
            background: 'rgba(255, 71, 87, 0.2)',
            border: '1px solid rgba(255, 71, 87, 0.4)',
            color: '#ff6b7a',
        }),
    },
}));

const ChatHeader = styled(Box)(({ theme }) => ({
    padding: '20px 24px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
    backdropFilter: 'blur(10px)',
    position: 'relative',
    zIndex: 9999, // Ensure header and its children (close button) are above other elements
    ...theme.applyStyles('dark', {
        borderBottom: '1px solid rgba(33, 39, 50, 0.3)',
        background: 'linear-gradient(135deg, rgba(26, 26, 46, 0.3) 0%, rgba(22, 33, 62, 0.3) 100%)',
    }),
    // Responsive design
    [theme.breakpoints.down('sm')]: {
        padding: '16px 20px',
    },
    [theme.breakpoints.down(600)]: {
        padding: '14px 16px',
    },
    '@media (max-width: 400px)': {
        padding: '12px 14px',
    },
}));

const ChatTitle = styled(Box)(({ theme }) => ({
    paddingLeft: '40px',
    fontSize: '1.2rem',
    fontWeight: '600',
    fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
    letterSpacing: '0.3px',
    color: '#2d3748',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
    ...theme.applyStyles('dark', {
        color: '#e2e8f0',
        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
    }),
    // Responsive design
    [theme.breakpoints.down('sm')]: {
        fontSize: '1.1rem',
    },
    [theme.breakpoints.down(600)]: {
        fontSize: '1rem',
    },
    '@media (max-width: 400px)': {
        fontSize: '0.95rem',
    },
}));

const RAGChatWindow = ({messages, setMessages, onClose, isOpen, chatId, setChatId}) => {
    const userName = "Sean Park";
    const [isVisible, setIsVisible] = useState(false);
    const chatWindowRef = useRef(null);
    const [tempResponse, setTempResponse] = useState('');
    const [settings, setSettings] = useState(getDefaultSettings());
    const [context, setContext] = useState([]);
    const contextRef = useRef({context:[],query:''});
    const [contextPopoverState, setContextPopoverState] = useState({ open: false, anchorEl: null, context: [],query:'' });
    const [settingsPopoverState, setSettingsPopoverState] = useState({ open: false, anchorEl: null });



    const handleClose = () => {
        if(isFullscreen){
            toggleFullscreen();
        }
        onClose();
    };
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsVisible(true);
        }, 100);

        return () => clearTimeout(timer);
    }, []);

    const [isFullscreen, setIsFullscreen] = useState(false);

    const handleResizeStart = (e) => {
        e.preventDefault();
        toggleFullscreen();
    };

    const toggleFullscreen = () => {
        if (!chatWindowRef.current) return;

        if (!isFullscreen) {
            // Make fullscreen - cover entire viewport
            chatWindowRef.current.style.position = 'fixed';
            chatWindowRef.current.style.width = '100vw';
            chatWindowRef.current.style.height = '100vh';
            chatWindowRef.current.style.left = '0px';
            chatWindowRef.current.style.right = '0px';
            chatWindowRef.current.style.bottom = '0px';
            chatWindowRef.current.style.top = '0px';
            chatWindowRef.current.style.borderRadius = '0px';
            chatWindowRef.current.style.margin = '0px';
            chatWindowRef.current.style.padding = '0px';
            chatWindowRef.current.style.maxWidth = 'none';
            chatWindowRef.current.style.maxHeight = 'none';
            chatWindowRef.current.style.minWidth = 'none';
            chatWindowRef.current.style.minHeight = 'none';
            chatWindowRef.current.style.zIndex = '12000';
            setIsFullscreen(true);
        } else {
            // Restore to normal size - clear all inline styles
            chatWindowRef.current.style.position = '';
            chatWindowRef.current.style.width = '';
            chatWindowRef.current.style.height = '';
            chatWindowRef.current.style.left = '';
            chatWindowRef.current.style.right = '';
            chatWindowRef.current.style.bottom = '';
            chatWindowRef.current.style.top = '';
            chatWindowRef.current.style.borderRadius = '';
            chatWindowRef.current.style.margin = '';
            chatWindowRef.current.style.padding = '';
            chatWindowRef.current.style.maxWidth = '';
            chatWindowRef.current.style.maxHeight = '';
            chatWindowRef.current.style.minWidth = '';
            chatWindowRef.current.style.minHeight = '';
            chatWindowRef.current.style.zIndex = '';
            setIsFullscreen(false);
        }
    };

    // Resize functionality now uses click-to-toggle fullscreen instead of drag-to-resize

    useEffect(() => {
        console.log(messages);
    }, [messages]);


    const handleMessageSend = (messageBody) => {
        const newMessage = {
            messageBody,
            type: 'User'
        };
        const loadingMessage = {
            messageBody: '',
            type: 'Loading'
        };
        setMessages((prev) => [...prev, newMessage, loadingMessage]);
        // setContext([]);

        ChatService.streamChat(messageBody, chatId,settings, 
            //on Chunk Received
        (chunk) => {
            //add the chunk to the last message
            setTempResponse(chunk);
            // console.log("current Context", contextRef.current);
        }, 
        //on Done
        (chunk,chatId) => {
            //onDone
            setChatId(chatId);
            console.log("DONE", chunk, contextRef.current);
            setMessages((prev) => {
                const updated = [...prev];
                if (updated.length > 0) {
                    updated[updated.length - 1] = {
                        messageBody: chunk,
                        type: 'AI',
                        context: contextRef.current.context,
                        query: contextRef.current.query,
                    };
                }
                return updated;
            });
            setTempResponse('');
            // setContext([]);
        }, 
        //on Error
        (error) => {
            console.error("Error on streamChat", error);
            // setMessages(
            //     [...messages, newMessage, {messageBody: "❌ Error Connecting to AI, please try again later."+error?.message||'', type: 'System'}]
            // );
            // setTempResponse('');
            // setContext([]);
        },
           //on Context Received
        (newContext,query) => {
            console.log("CONTEXT", newContext);
            contextRef.current = {context:newContext,query:query};
            setContext({context:newContext,query:query});
        },
    );
    };

    const handleOpenContext = (event, ctx,query) => {
        setContextPopoverState({ open: true, anchorEl: event.currentTarget, context: ctx || [],query:query });
    };

    const handleCloseContext = () => {
        setContextPopoverState((prev) => ({ ...prev, open: false, anchorEl: null }));
    };

    const handleOpenSettings = (event) => {
        setSettingsPopoverState({ open: true, anchorEl: event.currentTarget });
    };

    const handleCloseSettings = () => {
        setSettingsPopoverState((prev) => ({ ...prev, open: false, anchorEl: null }));
    };

    return (
        <ChatWindow isVisible={isOpen} ref={chatWindowRef}>
            <ChatHeader>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px',backgroundColor: 'transparent' }}>
                <FullscreenButton 
                onClick={handleResizeStart} 
                isFullscreen={isFullscreen}
                startIcon={isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            />
                    {/* <img src={AILogo} alt="AI Logo" style={{ width: '30px', height: '30px', paddingLeft: '40px'  }} /> */}
                    <ChatTitle>IntelliCampus AI Assistant</ChatTitle>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <CloseButton onClick={handleOpenSettings} title="Settings" aria-label="Settings">
                        <SettingsIcon />
                    </CloseButton>
                    <CloseButton onClick={handleClose} title="Close chat" aria-label="Close chat">
                        <CloseIcon />
                    </CloseButton>
                </div>
            </ChatHeader>
            <ChatScrollArea>
                {messageFeed(messages, tempResponse, handleOpenContext)}
            </ChatScrollArea>
            <ChatInput onSend={handleMessageSend} />
            <ContextPopover
                open={contextPopoverState.open}
                anchorEl={contextPopoverState.anchorEl}
                onClose={handleCloseContext}
                context={contextPopoverState.context}
                query={contextPopoverState.query}
            />
            <SettingsPopover
                open={settingsPopoverState.open}
                anchorEl={settingsPopoverState.anchorEl}
                onClose={handleCloseSettings}
                settings={settings}
                updateSettings={setSettings}
            />
          
        </ChatWindow>
    );
};

export default RAGChatWindow;